/**
 * Formula evaluation for bonus values — port of
 * `reference/compcon-cli/src/lib/evaluateModifier.ts`, generalized to the full
 * set of substitution variables the spec calls for: `{grit} {ll} {hull} {agi}
 * {sys} {eng}` (the cli only knew `{ll}` and `{grit}`).
 *
 * A bonus value is one of:
 *   - a `number` — used verbatim (ceil'd to match COMP/CON's `Bonus.Evaluate`);
 *   - a `string` — an arithmetic expression in the substitution variables, e.g.
 *     `"{grit}"`, `"{hull}+1"`, `"{ll}*2"`;
 *   - an array of either — summed (COMP/CON accepts arrays; no real data uses
 *     them, but we mirror the cli's behavior to stay faithful).
 *
 * The expression grammar is deliberately tiny and self-contained (no external
 * math-expression dependency): after `{var}` substitution we evaluate a pure
 * arithmetic expression over `+ - * / ( )` and non-negative decimals via a
 * recursive-descent parser. This keeps evaluation deterministic and dependency-
 * free, which matters because **the same result must be reproduced on every
 * client and the server** — the wire carries raw deltas and every participant
 * recomputes locally.
 */

/** The variables substitutable into a bonus formula. */
export type ModifierVars = {
  grit: number;
  ll: number;
  hull: number;
  agi: number;
  sys: number;
  eng: number;
}

const VAR_NAMES: (keyof ModifierVars)[] = ["grit", "ll", "hull", "agi", "sys", "eng"];

export type ModifierValue = number | string | (number | string)[];

/** Evaluate a bonus value against the given variable context, returning a number. */
export function evaluateModifier(modifier: ModifierValue, vars: ModifierVars): number {
  if (typeof modifier === "number") return Math.ceil(modifier);

  if (Array.isArray(modifier)) {
    return modifier.reduce<number>((sum, v) => sum + evaluateModifier(v, vars), 0);
  }

  // string expression: substitute {var} tokens, then evaluate arithmetic.
  let expr = modifier;
  for (const name of VAR_NAMES) {
    expr = expr.split(`{${name}}`).join(String(vars[name]));
  }
  return evalArithmetic(expr);
}

/**
 * Evaluate a pure arithmetic expression (`+ - * / ( )` and decimal literals).
 * Recursive-descent; throws on any unexpected token so a malformed formula
 * fails loudly rather than silently producing a wrong stat.
 */
function evalArithmetic(input: string): number {
  let pos = 0;
  const src = input;

  function skipWs(): void {
    while (pos < src.length && /\s/.test(src[pos]!)) pos++;
  }

  // expr := term (('+' | '-') term)*
  function parseExpr(): number {
    let value = parseTerm();
    for (;;) {
      skipWs();
      const op = src[pos];
      if (op === "+" || op === "-") {
        pos++;
        const rhs = parseTerm();
        value = op === "+" ? value + rhs : value - rhs;
      } else {
        return value;
      }
    }
  }

  // term := factor (('*' | '/') factor)*
  function parseTerm(): number {
    let value = parseFactor();
    for (;;) {
      skipWs();
      const op = src[pos];
      if (op === "*" || op === "/") {
        pos++;
        const rhs = parseFactor();
        value = op === "*" ? value * rhs : value / rhs;
      } else {
        return value;
      }
    }
  }

  // factor := ('+' | '-') factor | '(' expr ')' | number
  function parseFactor(): number {
    skipWs();
    const ch = src[pos];
    if (ch === "+") {
      pos++;
      return parseFactor();
    }
    if (ch === "-") {
      pos++;
      return -parseFactor();
    }
    if (ch === "(") {
      pos++;
      const value = parseExpr();
      skipWs();
      if (src[pos] !== ")") throw new Error(`evaluateModifier: expected ')' in "${input}"`);
      pos++;
      return value;
    }
    return parseNumber();
  }

  function parseNumber(): number {
    skipWs();
    const start = pos;
    while (pos < src.length && /[0-9.]/.test(src[pos]!)) pos++;
    if (pos === start) {
      throw new Error(`evaluateModifier: unexpected token at ${pos} in "${input}"`);
    }
    const n = Number(src.slice(start, pos));
    if (Number.isNaN(n)) throw new Error(`evaluateModifier: bad number in "${input}"`);
    return n;
  }

  const result = parseExpr();
  skipWs();
  if (pos !== src.length) {
    throw new Error(`evaluateModifier: trailing tokens in "${input}"`);
  }
  return result;
}
